import { ServerTenantTheme } from 'api/tenantDirectory';

import { Theme } from './themeProvider';

export type DerivedThemeExtras = {
  logoUrl?: string;
  faviconUrl?: string;
  loginBackgroundUrl?: string;
};

const clamp = (value: number) => Math.min(255, Math.max(0, Math.round(value)));

const parseHex = (hex: string): [number, number, number] => {
  const clean = hex.replace('#', '');
  return [parseInt(clean.slice(0, 2), 16), parseInt(clean.slice(2, 4), 16), parseInt(clean.slice(4, 6), 16)];
};

const toHex = (r: number, g: number, b: number): string =>
  `#${[r, g, b].map(v => clamp(v).toString(16).padStart(2, '0')).join('')}`;

/** Mixes a #RRGGBB colour toward white (positive percent) or black (negative). */
export const shade = (hex: string, percent: number): string => {
  const [r, g, b] = parseHex(hex);
  const target = percent > 0 ? 255 : 0;
  const amount = Math.abs(percent) / 100;
  return toHex(r + (target - r) * amount, g + (target - g) * amount, b + (target - b) * amount);
};

/** YIQ luma contrast: white text on dark backgrounds, black on light. */
export const readableTextColor = (hex: string): '#FFFFFF' | '#000000' => {
  const [r, g, b] = parseHex(hex);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 150 ? '#000000' : '#FFFFFF';
};

/**
 * Overlays the server-provided core branding (two colours + asset urls) onto a bundled
 * base theme, deriving the portal's wider palette (buttons, header/footer, loader,
 * charts) so ThemeProvider consumers are unchanged. Fields the server doesn't cover
 * (fonts, widget images, logo dimensions) come from the base theme.
 */
export const applyServerTheme = (server: ServerTenantTheme, base: Theme): Theme & DerivedThemeExtras => {
  const primary = server.primaryColor;
  const secondary = server.secondaryColor ?? server.primaryColor;

  return {
    ...base,
    headerColor: primary,
    headerTextColor: readableTextColor(primary),
    footerColor: primary,
    footerTextColor: readableTextColor(primary),
    loader: secondary,
    indicatorColor: secondary,
    buttons: {
      primary: {
        color: primary,
        textColor: readableTextColor(primary),
        hoverColor: shade(primary, 20),
        hoverText: readableTextColor(shade(primary, 20)),
      },
      secondary: {
        color: secondary,
        textColor: readableTextColor(secondary),
        hoverColor: shade(secondary, 20),
        hoverText: readableTextColor(shade(secondary, 20)),
      },
    },
    charts: {
      v1: { ...base.charts.v1, color: primary, bgColor: shade(primary, 85) },
      v2: { ...base.charts.v2, color: secondary, bgColor: shade(secondary, 85) },
    },
    logoUrl: server.logoUrl ?? undefined,
    faviconUrl: server.faviconUrl ?? undefined,
    loginBackgroundUrl: server.loginBackgroundUrl ?? undefined,
  };
};
